// Staggered page transition between routes (presentation only — never touches progress).
// The visible top-level blocks of the old page (children of main.page) leave one after
// another, the route content is swapped, then the visible blocks of the new page arrive in
// sequence. Each block gets --i (its index); the delay is min(--i × 70ms, 350ms).
//   leave : opacity 1→0, translateY(10px) scale(.94), 250ms ease-in
//   arrive: the reverse, 450ms cubic-bezier(.32,.72,0,1)
// With the View Transitions API (+ view-transition-class) the blocks become named snapshots
// sharing the class "rz-block"; ::view-transition-old/new(*.rz-block) carry the --i delay.
// Otherwise the same keyframes run on the live blocks (see 22-page-transition.css).
// Only opacity and transform animate; #app ignores the pointer while it runs.
// prefers-reduced-motion: one quick cross-fade. Automated tests (window.__NELL_FAST__) get an
// instant swap unless window.__RZ_TX__ is set ('auto' | 'css' to force the CSS path).
import { prefersReducedMotion } from './carousel.js';

export const TX = { leave: 250, arrive: 450, step: 70, cap: 350, fade: 180 };
const stagger = (n) => Math.min(Math.max(n - 1, 0) * TX.step, TX.cap);
const html = () => document.documentElement;

function mode() {
  const force = window.__RZ_TX__;
  if (window.__NELL_FAST__ && !force) return 'off';
  const vt = typeof document.startViewTransition === 'function' && !!globalThis.CSS?.supports?.('view-transition-class', 'rz-block');
  if (prefersReducedMotion()) return vt && force !== 'css' ? 'vt-fade' : 'css-fade';
  return vt && force !== 'css' ? 'vt' : 'css';
}

/** Top-level blocks of the current page that are (at least partly) on screen. */
function visibleBlocks(page) {
  if (!page) return [];
  const vh = window.innerHeight;
  return [...page.children].filter((el) => {
    if (el.matches('script, style, template, [hidden]')) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < vh;
  });
}
const mark = (blocks) => blocks.forEach((el, i) => { el.style.setProperty('--i', String(i)); el.setAttribute('data-tx-block', ''); });
const unmark = (root) => root.querySelectorAll('[data-tx-block]').forEach((el) => {
  el.style.removeProperty('--i'); el.style.removeProperty('view-transition-name'); el.style.removeProperty('view-transition-class'); el.removeAttribute('data-tx-block');
});
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {HTMLElement} root  the #app element
 * @param {() => void} render the router (replaces the page, or redirects, or navigates in place)
 * @param {() => boolean} inPlace true when the next route updates the current page without replacing it
 */
export function createPageTransition(root, render, inPlace) {
  const page = () => root.querySelector('main.page');
  let state = 'idle'; // idle | leaving | gone
  let token = 0;
  let arriveTimer = 0;
  let vt = null;

  const busy = (on) => html().classList.toggle('rz-tx-busy', on);
  function settle() {
    clearTimeout(arriveTimer);
    root.classList.remove('rz-tx-out', 'rz-tx-in', 'rz-tx-fade-out', 'rz-tx-fade-in');
    unmark(root);
    html().classList.remove('rz-tx-vt', 'rz-tx-vt-fade');
    html().style.removeProperty('--tx-leave');
    document.getElementById('rz-tx-vt-style')?.remove();
    root.dataset.tx = 'idle';
    busy(false);
  }

  // ---------------------------------------------------------------- CSS keyframes path
  async function cssNavigate(fade) {
    const my = ++token;
    const before = page();
    if (state !== 'gone') {
      settle();
      const blocks = fade ? [] : visibleBlocks(before);
      if (!fade && !blocks.length) { render(); return; }
      state = 'leaving'; busy(true); root.dataset.tx = 'leave';
      if (fade) root.classList.add('rz-tx-fade-out');
      else { mark(blocks); root.classList.add('rz-tx-out'); }
      await wait(fade ? TX.fade * 0.6 : TX.leave + stagger(blocks.length));
      if (my !== token) return; // a newer navigation took over
      state = 'gone';
    }
    render();
    if (page() === before) { // redirected (a new hashchange follows) — stay gone briefly, then recover
      setTimeout(() => { if (my === token && state === 'gone') { state = 'idle'; settle(); } }, 400);
      return;
    }
    state = 'idle';
    root.classList.remove('rz-tx-out', 'rz-tx-fade-out');
    root.dataset.tx = 'arrive';
    if (fade) {
      root.classList.add('rz-tx-fade-in');
      arriveTimer = setTimeout(settle, TX.fade);
    } else {
      const blocks = visibleBlocks(page());
      mark(blocks); root.classList.add('rz-tx-in');
      arriveTimer = setTimeout(settle, TX.arrive + stagger(blocks.length) + 30);
    }
  }

  // ---------------------------------------------------------------- View Transitions path
  function vtNavigate(fade) {
    const my = ++token;
    if (vt) { try { vt.skipTransition(); } catch { /* finished */ } }
    settle(); state = 'idle';
    const before = page();
    const oldBlocks = fade ? [] : visibleBlocks(before);
    if (!fade && !oldBlocks.length) { render(); return; }
    const rules = [];
    oldBlocks.forEach((el, i) => {
      el.style.setProperty('view-transition-name', `rz-o${i}`); el.style.setProperty('view-transition-class', 'rz-block');
      rules.push(`::view-transition-old(rz-o${i}){--i:${i}}`);
    });
    mark(oldBlocks);
    html().classList.add(fade ? 'rz-tx-vt-fade' : 'rz-tx-vt');
    html().style.setProperty('--tx-leave', `${fade ? 0 : TX.leave + stagger(oldBlocks.length)}ms`);
    busy(true); root.dataset.tx = 'vt';
    let changed = true;
    vt = document.startViewTransition(() => {
      unmark(root);
      render();
      if (page() === before) { changed = false; return; }
      if (fade) return;
      const nb = visibleBlocks(page());
      nb.forEach((el, i) => {
        el.style.setProperty('view-transition-name', `rz-n${i}`); el.style.setProperty('view-transition-class', 'rz-block');
        rules.push(`::view-transition-new(rz-n${i}){--i:${i}}`);
      });
      mark(nb);
      const st = document.getElementById('rz-tx-vt-style') || document.head.appendChild(Object.assign(document.createElement('style'), { id: 'rz-tx-vt-style' }));
      st.textContent = rules.join('\n');
    });
    vt.ready.then(() => { if (!changed) vt.skipTransition(); }).catch(() => {});
    vt.finished.finally(() => { if (my === token) { vt = null; settle(); } });
  }

  function navigate() {
    if (state === 'leaving') return; // the swap will render whatever the hash is by then
    const m = mode();
    if (m === 'off' || !page() || inPlace()) { token++; if (state === 'gone') { state = 'idle'; } settle(); render(); return; }
    if (m === 'vt' || m === 'vt-fade') vtNavigate(m === 'vt-fade');
    else cssNavigate(m === 'css-fade');
  }
  root.dataset.tx = 'idle';
  return { navigate };
}
